/**
 * Web join landing — makes group invite links work WITHOUT the app installed
 * (church & youth-group virality plan, backlog item #1).
 *
 * Invite links shared from the app point here:
 *   https://jesus-entered-the-chat.vercel.app/join?token=<inviteToken>
 *
 * The page tries the native deep link (jetc://join?token=…) for people who
 * already have the app, and shows warm install/web fallbacks for everyone
 * else, so an invite dropped into a group chat never dead-ends.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";

const APP_SCHEME = "jetc";
const PREVIEW_URL = "https://jetc-app-preview.vercel.app";
const APP_STORE_URL = "https://apps.apple.com/us/app/jesus-entered-the-chat/id6788132522";

export default function Join() {
  const [params] = useSearchParams();
  const token = (params.get("token") ?? "").trim();
  const [triedApp, setTriedApp] = useState(false);

  const deepLink = useMemo(
    () => `${APP_SCHEME}://join?token=${encodeURIComponent(token)}`,
    [token],
  );

  // Attempt to bounce straight into the installed app once on load.
  useEffect(() => {
    if (!token) return;
    const t = setTimeout(() => setTriedApp(true), 1200);
    window.location.href = deepLink;
    return () => clearTimeout(t);
  }, [token, deepLink]);

  return (
    <main className="min-h-screen bg-[#f6f1e7] flex items-center justify-center px-6 py-16">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-[#e8dfc9] p-8 text-center">
        <div className="text-5xl mb-4" aria-hidden>
          🕊️
        </div>
        <h1 className="font-serif text-2xl text-[#2b2418] mb-2">
          You&apos;ve been invited to a circle
        </h1>

        {token ? (
          <>
            <p className="text-[#6b5d3f] text-sm leading-relaxed mb-6">
              Someone wants to walk with you in <strong>Jesus Entered the Chat</strong> — daily
              verses, the Fruits of the Spirit, and a small group keeping each other encouraged.
            </p>

            <a
              href={deepLink}
              className="block w-full rounded-xl bg-[#3b3361] text-white font-semibold py-3 mb-3 hover:opacity-90 transition"
            >
              Open in the app
            </a>

            <a
              href={`${PREVIEW_URL}?join=${encodeURIComponent(token)}`}
              className="block w-full rounded-xl border border-[#e8dfc9] text-[#2b2418] font-semibold py-3 mb-3 hover:bg-[#f6f1e7] transition"
            >
              Continue in your browser
            </a>

            {triedApp ? (
              <div className="space-y-3">
                <p className="text-xs text-[#8a7d5e] leading-relaxed">
                  Don&apos;t have the app yet? Install JETC from the App Store, then return
                  to this invitation to open your circle.
                </p>
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="block text-sm font-semibold text-[#3b3361] underline underline-offset-4"
                >
                  Download JETC on the App Store
                </a>
                <Link
                  to="/download"
                  className="block text-xs font-medium text-[#6b5d3f] underline underline-offset-4"
                >
                  See TestFlight joining instructions
                </Link>
              </div>
            ) : (
              <p className="text-xs text-[#8a7d5e]">Opening your circle…</p>
            )}
          </>
        ) : (
          <>
            <p className="text-[#6b5d3f] text-sm leading-relaxed mb-6">
              This invite link is missing its code. Ask the person who sent it to share it again
              from the Groups tab — or start your own journey today.
            </p>
            <Link
              to="/"
              className="block w-full rounded-xl bg-[#3b3361] text-white font-semibold py-3 hover:opacity-90 transition"
            >
              Explore Jesus Entered the Chat
            </Link>
          </>
        )}
      </div>
    </main>
  );
}
